import { ArrowLeft, Ban, CheckCircle2, Loader2, XCircle } from 'lucide-react'
import type { MouseEvent } from 'react'

import { GeneratedReportType, type ReportGenerationJobLastRun } from '@ankor-io/common/types/reports/generated-report'
import {
  Badge,
  Button,
  CopyToClipboard,
  Loading,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ankor-io/ui-components'

import { useReportGenerationJobsQuery } from '@/queries/organization'
import { formatDate, formatDurationFromDates } from '@/utils/format'

import { statusBadge } from '../integration/integration-status'

const formatReportType = (type: string) => {
  return type
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ')
}

const lastRunIcon = (lastRun?: ReportGenerationJobLastRun) => {
  if (!lastRun) return <Ban className="size-4 text-gray-400" />

  switch (lastRun.status) {
    case 'COMPLETED':
      return <CheckCircle2 className="size-4 text-green-500" />
    case 'FAILED':
      return <XCircle className="size-4 text-red-500" />
    case 'IN_PROGRESS':
      return <Loader2 className="size-4 text-yellow-500 animate-spin" />
    default:
      return <Ban className="size-4 text-gray-400" />
  }
}

export const ReportGenerationJobsView = ({
  organizationId,
  onBack,
  onSelectJob,
  onSelectLastRun,
}: {
  organizationId: string
  onBack: () => void
  onSelectJob?: (jobId: string) => void
  onSelectLastRun?: (jobId: string, runId: string) => void
}) => {
  const { data: jobs, isPending, error } = useReportGenerationJobsQuery(organizationId)

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button
            plain
            onClick={onBack}
            className="flex items-center"
          >
            <ArrowLeft data-slot="icon" />
            Back
          </Button>
          <span className="text-gray-300 dark:text-gray-600">/</span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">Report Generation — Jobs</span>
          {jobs && (
            <Badge
              color="gray"
              className="whitespace-nowrap"
            >
              {jobs.length} jobs
            </Badge>
          )}
        </div>
      </div>

      {isPending && <Loading variant="spinner" />}
      {error && <div className="text-sm text-red-500">Error: {error.message}</div>}

      {jobs && jobs.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">No report generation jobs found.</p>
      )}

      {jobs && jobs.length > 0 && (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>File Name</TableHeader>
              <TableHeader>Type</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Started</TableHeader>
              <TableHeader>Duration</TableHeader>
              <TableHeader>Last Run</TableHeader>
              <TableHeader>Action</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow
                key={job.id}
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                onClick={() => onSelectJob?.(job.id)}
              >
                <TableCell className="text-sm font-medium text-gray-900 dark:text-white max-w-[220px] truncate">
                  {job.fileName}
                </TableCell>
                <TableCell className="text-sm text-gray-600 dark:text-gray-400">
                  {job.type === GeneratedReportType.XERO_BANK_STATEMENT
                    ? 'Xero Bank Statement'
                    : formatReportType(job.type)}
                </TableCell>
                <TableCell>{statusBadge(job.status)}</TableCell>
                <TableCell className="tabular-nums text-gray-500">{formatDate(job.startedAt)}</TableCell>
                <TableCell className="tabular-nums text-gray-500">
                  {formatDurationFromDates(job.startedAt, job.finishedAt)}
                </TableCell>
                <TableCell>
                  {job.lastRun ? (
                    <Button
                      plain
                      className="border-0 p-0.5 shadow-none before:hidden after:hidden hover:opacity-70 data-hover:bg-transparent data-active:bg-transparent dark:data-hover:bg-transparent dark:data-active:bg-transparent"
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation()
                        onSelectLastRun?.(job.id, job.lastRun!.id)
                      }}
                    >
                      {lastRunIcon(job.lastRun)}
                    </Button>
                  ) : (
                    lastRunIcon()
                  )}
                </TableCell>
                <TableCell>
                  <div onClick={(e) => e.stopPropagation()}>
                    <CopyToClipboard
                      text={job.id}
                      tooltip="Copy Job ID"
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  )
}
